import { Box, Button, Skeleton, Text } from "@mantine/core"
import { PlusIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { currencyFormat } from "../../../utils"
import { ReasonTooltip } from "../../ui"
import classes from "./catalog.module.css"
import { flyToCart } from "./fly-to-cart"
import { addBlockReason, promoIcon, type PriceView } from "./price-labels"
import { ProductThumb } from "./ProductThumb"
import { QtyStepper } from "./QtyStepper"
import { StockLabel } from "./StockLabel"
import { cleanName, sellsWithoutStock } from "./utils"

type Props = {
  product: any
  view: PriceView
  /** Price-service round-trip still in flight — show a placeholder, not 0₮. */
  priceLoading: boolean
  cartQty: number
  stock: number
  minOrder: number
  /** «Бүх нийлүүлэгч» view: name the supplier on the card (B20). */
  showSupplier: boolean
  onAdd: (quantity: number) => void
  onOpen: () => void
}

function ProductCardInner({ product, view, priceLoading, cartQty, stock, minOrder, showSupplier, onAdd, onOpen }: Props) {
  const { t } = useTranslation("order")
  const imageRef = React.useRef<HTMLDivElement>(null)
  const sellWithoutStock = sellsWithoutStock(product)
  const [draft, setDraft] = React.useState(minOrder)

  React.useEffect(() => setDraft(minOrder), [minOrder])

  // What is still orderable on top of what the cart already holds.
  const remaining = sellWithoutStock ? undefined : Math.max(stock - cartQty, 0)
  const blockReason =
    priceLoading && view.price === 0
      ? null
      : addBlockReason(t, { price: view.price, stock, minOrder, sellWithoutStock }) ??
        (remaining !== undefined && cartQty > 0 && remaining <= 0
          ? t("productList.stockLimitReached", { count: cartQty, max: stock })
          : null)
  const blocked = !!blockReason || (priceLoading && view.price === 0)

  const brandName = cleanName(product.brand?.name || product.item?.brand?.name || (typeof product.brand === "string" ? product.brand : ""))
  const brandLine = [
    product.isConsignment ? t("productList.consignment") : "",
    brandName,
    showSupplier ? cleanName(product.supplierName) : "",
  ]
    .filter(Boolean)
    .join(" · ")
  const name = cleanName(typeof product.nameMon === "string" ? product.nameMon : "")

  const handleAdd = () => {
    if (blocked) return
    const qty = remaining !== undefined ? Math.min(draft, remaining) : draft
    if (qty <= 0) return
    onAdd(qty)
    flyToCart(imageRef.current)
  }

  return (
    <Box className={classes.productCard}>
      <Box
        ref={imageRef}
        className={classes.imageBox}
        role="link"
        tabIndex={0}
        aria-label={name}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter") onOpen()
        }}
      >
        <ProductThumb src={product.image} alt={name} withCaption />
        {view.tag && (
          <Box className={classes.imageTagLeft}>
            <Box component="span" className={`${classes.pill} ${classes.pillOnImage}`}>
              {promoIcon(view.tag.kind)}
              {view.tag.label}
            </Box>
          </Box>
        )}
        {cartQty > 0 && (
          <Box className={classes.imageTagRight}>
            <Box component="span" className={`${classes.pill} ${classes.pillDark}`}>
              {t("catalog.inCart", { count: cartQty })}
            </Box>
          </Box>
        )}
      </Box>

      <Text className={classes.brandLine}>{brandLine || "-"}</Text>
      <Text className={classes.productName} title={name} onClick={onOpen}>
        {name || "-"}
      </Text>

      <Box mih={44}>
        {priceLoading && view.price === 0 ? (
          <Skeleton h={24} w="60%" radius="sm" mt={4} />
        ) : view.price > 0 ? (
          <>
            <Box className={classes.priceLine}>
              <Text span className={classes.price}>
                {currencyFormat(view.price)}
              </Text>
              {view.strike && (
                <Text span className={classes.strike}>
                  {currencyFormat(view.strike)}
                </Text>
              )}
              <Text span className={classes.priceSource} data-promo={view.sourceIsPromo || undefined} title={view.source}>
                {view.source}
              </Text>
            </Box>
          </>
        ) : view.unavailable ? (
          <>
            <Text className={classes.price}>-</Text>
            <Text className={classes.source}>{view.source}</Text>
          </>
        ) : (
          <>
            <Text fw={650} fz="var(--nw-fz-lead)" c="var(--nw-text)" mt={4}>
              {t("catalog.priceUnknown")}
            </Text>
            <Text className={classes.source}>{t("catalog.priceUnknownHint")}</Text>
          </>
        )}
      </Box>

      <Box className={classes.stockRow}>
        <StockLabel stock={stock} sellWithoutStock={sellWithoutStock} />
        <Text span className={classes.sku} title={product.skuCode || undefined} translate="no">
          {product.skuCode || "-"}
        </Text>
      </Box>

      <Box className={classes.cardFooter}>
        <QtyStepper
          value={draft}
          onChange={setDraft}
          min={minOrder}
          max={remaining !== undefined && remaining >= minOrder ? remaining : undefined}
          disabled={blocked}
          label={t("catalog.qtyLabel")}
          onEnter={handleAdd}
        />
        <ReasonTooltip reason={blockReason}>
          {blockReason ? (
            // Same «Сагсанд» button, shown disabled; the reason stays in the tooltip.
            <Button color="dark" radius="md" h={38} px="xs" fullWidth leftSection={<PlusIcon size={14} />}>
              {t("catalog.toCart")}
            </Button>
          ) : cartQty > 0 ? (
            <Button variant="default" radius="md" h={38} px="xs" fullWidth leftSection={<PlusIcon size={14} />} disabled={blocked} onClick={handleAdd}>
              {t("catalog.addMore")}
            </Button>
          ) : (
            <Button color="dark" radius="md" h={38} px="xs" fullWidth leftSection={<PlusIcon size={14} />} disabled={blocked} onClick={handleAdd}>
              {t("catalog.toCart")}
            </Button>
          )}
        </ReasonTooltip>
      </Box>
    </Box>
  )
}

export const ProductCard = React.memo(ProductCardInner)
